// Dictionary, part 1 — the envelope (ISA/GS/ST/SE/GE/IEA), the 850's header
// body, and the Name (N1) loop. Generic X12 850 (5010) definitions only; not
// tied to any trading partner's implementation guide.
//
// Several segments defined here (REF, PER, TAX, FOB, CTP, ITD, DTM, LDT, MSG,
// ISS, PCT, PWK, N1-N4) are reused verbatim inside the PO1 item loop and its
// sub-loops too — X12 defines one segment per id, reused across loops, so
// dictionary/item-loop-segments.ts does not redefine them. Which loop a given
// occurrence actually sits in is a structural fact the parser determines from
// segment order, not from this file.

import {LoopId, type ElementDefinition, type SegmentDefinition} from './types';
import {FUNCTIONAL_IDENTIFIER_CODES} from './functional-identifier-code';
import {DATE_TIME_QUALIFIER_CODES} from './date-time-qualifiers';
import {TRANSACTION_SET_PURPOSE_CODES} from './transaction-set-purpose-codes';
import {REFERENCE_IDENTIFICATION_QUALIFIER_CODES} from './reference-identification-qualifiers';
import {ENTITY_IDENTIFIER_CODES} from './entity-identifier-codes';
import {IDENTIFICATION_CODE_QUALIFIER_CODES} from './identification-code-qualifiers';
import {SHIPMENT_METHOD_OF_PAYMENT_CODES} from './shipment-method-of-payment-codes';
import {CONTACT_FUNCTION_CODES} from './contact-function-codes';
import {COMMUNICATION_NUMBER_QUALIFIER_CODES} from './communication-number-qualifiers';
import {PRICE_IDENTIFIER_CODES} from './price-identifier-codes';
import {BASIS_OF_UNIT_PRICE_CODES} from './basis-of-unit-price-codes';

function el(
  position: number,
  label: string,
  description: string,
  extra: Partial<ElementDefinition> = {}
): ElementDefinition {
  return {position, label, description, ...extra};
}

export const HEADER_SEGMENTS: Record<string, SegmentDefinition> = {
  ISA: {
    id: 'ISA',
    name: 'Interchange Control Header',
    purpose:
      'Opens the interchange envelope: identifies sender and receiver, the delimiters used throughout the document, and the control number matched by the closing IEA.',
    loop: LoopId.Envelope,
    required: true,
    elements: [
      el(
        1,
        'Authorization Information Qualifier',
        'Whether authorization information follows.',
        {
          dataType: 'code',
          codes: [
            {code: '00', label: 'No Authorization Information Present'},
            {code: '03', label: 'Additional Data Identification'},
            {code: 'ZZ', label: 'Mutually Defined'},
          ],
        }
      ),
      el(
        2,
        'Authorization Information',
        'Authorization data, or spaces when not used.'
      ),
      el(
        3,
        'Security Information Qualifier',
        'Whether security information follows.',
        {
          dataType: 'code',
          codes: [
            {code: '00', label: 'No Security Information Present'},
            {code: '01', label: 'Password'},
            {code: 'ZZ', label: 'Mutually Defined'},
          ],
        }
      ),
      el(4, 'Security Information', 'Security data, or spaces when not used.'),
      el(
        5,
        'Interchange ID Qualifier (Sender)',
        'How the sender ID is formatted/assigned.',
        {
          dataType: 'code',
          codes: [
            {code: '01', label: 'Duns (Dun & Bradstreet)'},
            {code: '02', label: 'SCAC (Standard Carrier Alpha Code)'},
            {code: '08', label: 'UCC EDI Communications ID (Comm ID)'},
            {code: '12', label: 'Phone (Telephone Companies)'},
            {code: '14', label: 'Duns Plus Suffix'},
            {code: '16', label: 'Duns Number With 4-Character Suffix'},
            {code: 'ZZ', label: 'Mutually Defined'},
          ],
        }
      ),
      el(
        6,
        'Interchange Sender ID',
        'Identifies the sender of this interchange.',
        {
          dataType: 'identifier',
          required: true,
        }
      ),
      el(
        7,
        'Interchange ID Qualifier (Receiver)',
        'How the receiver ID is formatted/assigned.',
        {
          dataType: 'code',
          codes: [
            {code: '01', label: 'Duns (Dun & Bradstreet)'},
            {code: '02', label: 'SCAC (Standard Carrier Alpha Code)'},
            {code: '08', label: 'UCC EDI Communications ID (Comm ID)'},
            {code: '12', label: 'Phone (Telephone Companies)'},
            {code: '14', label: 'Duns Plus Suffix'},
            {code: '16', label: 'Duns Number With 4-Character Suffix'},
            {code: 'ZZ', label: 'Mutually Defined'},
          ],
        }
      ),
      el(
        8,
        'Interchange Receiver ID',
        'Identifies the intended receiver of this interchange.',
        {
          dataType: 'identifier',
          required: true,
        }
      ),
      el(9, 'Interchange Date', 'Date this interchange was prepared.', {
        dataType: 'date',
        format: 'YYMMDD',
        required: true,
      }),
      el(10, 'Interchange Time', 'Time this interchange was prepared.', {
        dataType: 'time',
        format: 'HHMM',
        required: true,
      }),
      el(
        11,
        'Repetition Separator / Standards Identifier',
        'The repetition separator character (5010) — or the interchange control standards identifier in older versions.'
      ),
      el(
        12,
        'Interchange Control Version Number',
        'Which version of the interchange control envelope is in use.',
        {
          dataType: 'code',
          codes: [
            {code: '00401', label: 'Draft Standard for Trial Use — 004010'},
            {code: '00501', label: 'Standard — 005010'},
          ],
        }
      ),
      el(
        13,
        'Interchange Control Number',
        'Control number matched by IEA02 to confirm the interchange closed correctly.',
        {
          dataType: 'numeric',
          required: true,
        }
      ),
      el(
        14,
        'Acknowledgment Requested',
        'Whether a TA1 interchange acknowledgment is requested.',
        {
          dataType: 'code',
          codes: [
            {code: '0', label: 'No Acknowledgment Requested'},
            {code: '1', label: 'Interchange Acknowledgment Requested'},
          ],
        }
      ),
      el(
        15,
        'Usage Indicator',
        'Whether this interchange carries production or test data.',
        {
          dataType: 'code',
          required: true,
          codes: [
            {code: 'P', label: 'Production Data'},
            {code: 'T', label: 'Test Data'},
          ],
        }
      ),
      el(
        16,
        'Component Element Separator',
        'The character used to separate sub-elements within an element.'
      ),
    ],
  },

  GS: {
    id: 'GS',
    name: 'Functional Group Header',
    purpose:
      'Opens a functional group of one or more transaction sets of the same type.',
    loop: LoopId.Envelope,
    required: true,
    elements: [
      el(
        1,
        'Functional Identifier Code',
        'The type of transaction sets in this group.',
        {
          dataType: 'code',
          required: true,
          codes: FUNCTIONAL_IDENTIFIER_CODES,
        }
      ),
      el(
        2,
        "Application Sender's Code",
        'Identifies the sending application.',
        {required: true}
      ),
      el(
        3,
        "Application Receiver's Code",
        'Identifies the receiving application.',
        {required: true}
      ),
      el(4, 'Date', 'Date the functional group was prepared.', {
        dataType: 'date',
        format: 'CCYYMMDD',
        required: true,
      }),
      el(5, 'Time', 'Time the functional group was prepared.', {
        dataType: 'time',
        required: true,
      }),
      el(
        6,
        'Group Control Number',
        'Control number matched by GE02 to confirm the group closed correctly.',
        {
          dataType: 'numeric',
          required: true,
        }
      ),
      el(
        7,
        'Responsible Agency Code',
        'The agency that maintains this standard.',
        {
          dataType: 'code',
          codes: [{code: 'X', label: 'Accredited Standards Committee X12'}],
        }
      ),
      el(
        8,
        'Version/Release/Industry Identifier Code',
        'The X12 version this group is formatted against, e.g. 004010 or 005010, often with an industry extension appended (e.g. 004010VICS).',
        {required: true}
      ),
    ],
  },

  ST: {
    id: 'ST',
    name: 'Transaction Set Header',
    purpose: 'Opens one transaction set and identifies its type.',
    loop: LoopId.Envelope,
    required: true,
    elements: [
      el(
        1,
        'Transaction Set Identifier Code',
        'The transaction set type — 850 is a Purchase Order.',
        {
          dataType: 'code',
          required: true,
          codes: [
            {code: '850', label: 'Purchase Order'},
            {code: '855', label: 'Purchase Order Acknowledgment'},
            {code: '856', label: 'Ship Notice/Manifest'},
          ],
        }
      ),
      el(
        2,
        'Transaction Set Control Number',
        'Control number matched by SE02 to confirm this transaction closed correctly.',
        {
          required: true,
        }
      ),
      el(
        3,
        'Implementation Convention Reference',
        'Optional reference to a specific implementation guide version.'
      ),
    ],
  },

  SE: {
    id: 'SE',
    name: 'Transaction Set Trailer',
    purpose:
      'Closes a transaction set and states how many segments it contained.',
    loop: LoopId.Envelope,
    required: true,
    elements: [
      el(
        1,
        'Number of Included Segments',
        'Total segments in this transaction set, counting ST and SE themselves.',
        {
          dataType: 'numeric',
          required: true,
        }
      ),
      el(
        2,
        'Transaction Set Control Number',
        'Must match ST02 of the same transaction.',
        {required: true}
      ),
    ],
  },

  GE: {
    id: 'GE',
    name: 'Functional Group Trailer',
    purpose:
      'Closes a functional group and states how many transaction sets it contained.',
    loop: LoopId.Envelope,
    required: true,
    elements: [
      el(
        1,
        'Number of Transaction Sets Included',
        'Total ST...SE transaction sets in this group.',
        {
          dataType: 'numeric',
          required: true,
        }
      ),
      el(2, 'Group Control Number', 'Must match GS06 of the same group.', {
        dataType: 'numeric',
        required: true,
      }),
    ],
  },

  IEA: {
    id: 'IEA',
    name: 'Interchange Control Trailer',
    purpose:
      'Closes the interchange envelope and states how many functional groups it contained.',
    loop: LoopId.Envelope,
    required: true,
    elements: [
      el(
        1,
        'Number of Included Functional Groups',
        'Total GS...GE groups in this interchange.',
        {
          dataType: 'numeric',
          required: true,
        }
      ),
      el(
        2,
        'Interchange Control Number',
        'Must match ISA13 of the same interchange.',
        {
          dataType: 'numeric',
          required: true,
        }
      ),
    ],
  },

  BEG: {
    id: 'BEG',
    name: 'Beginning Segment for Purchase Order',
    purpose:
      'Identifies the purchase order itself — its number, type, purpose and date. Required at the start of every 850.',
    loop: LoopId.Header,
    required: true,
    elements: [
      el(
        1,
        'Transaction Set Purpose Code',
        'What this transmission of the PO is meant to do.',
        {
          dataType: 'code',
          required: true,
          codes: TRANSACTION_SET_PURPOSE_CODES,
        }
      ),
      el(2, 'Purchase Order Type Code', 'The kind of order this is.', {
        dataType: 'code',
        required: true,
        codes: [
          {code: 'NE', label: 'New Order'},
          {code: 'CO', label: 'Combined Order and Invoice'},
          {code: 'RO', label: 'Rush Order'},
          {code: 'SA', label: 'Stand-alone Order'},
          {code: 'DS', label: 'Do Not Substitute'},
        ],
      }),
      el(3, 'Purchase Order Number', "The buyer's own PO number.", {
        required: true,
      }),
      el(
        4,
        'Release Number',
        'Release number against a blanket/standing order, when used.'
      ),
      el(5, 'Date', 'Date the purchase order was written.', {
        dataType: 'date',
        format: 'CCYYMMDD',
        required: true,
      }),
    ],
  },

  CUR: {
    id: 'CUR',
    name: 'Currency',
    purpose:
      'States the currency the order is priced in, when it is not the receiver default.',
    loop: LoopId.Header,
    elements: [
      el(1, 'Entity Identifier Code', 'Whose currency this is.', {
        dataType: 'code',
        codes: ENTITY_IDENTIFIER_CODES.filter(
          c => c.code === 'BY' || c.code === 'SE'
        ),
      }),
      el(2, 'Currency Code', 'ISO 4217 currency code, e.g. USD.', {
        dataType: 'code',
        required: true,
      }),
    ],
  },

  REF: {
    id: 'REF',
    name: 'Reference Identification',
    purpose:
      'Carries a reference number of a kind not covered by a more specific segment — a department number, vendor number, contract number, etc.',
    loop: LoopId.Header,
    elements: [
      el(
        1,
        'Reference Identification Qualifier',
        'What kind of reference number follows.',
        {
          dataType: 'code',
          required: true,
          codes: REFERENCE_IDENTIFICATION_QUALIFIER_CODES,
        }
      ),
      el(2, 'Reference Identification', 'The reference number itself.', {
        required: true,
      }),
    ],
  },

  PER: {
    id: 'PER',
    name: 'Administrative Communications Contact',
    purpose: 'Names a contact person and how to reach them.',
    loop: LoopId.Header,
    elements: [
      el(1, 'Contact Function Code', 'The role this contact serves.', {
        dataType: 'code',
        required: true,
        codes: CONTACT_FUNCTION_CODES,
      }),
      el(2, 'Name', 'The contact person or department name.'),
      el(3, 'Communication Number Qualifier', 'What kind of number follows.', {
        dataType: 'code',
        codes: COMMUNICATION_NUMBER_QUALIFIER_CODES,
      }),
      el(
        4,
        'Communication Number',
        'The phone number, fax number or email address.'
      ),
      el(
        5,
        'Communication Number Qualifier',
        'What kind of number follows, for a second contact method.',
        {dataType: 'code', codes: COMMUNICATION_NUMBER_QUALIFIER_CODES}
      ),
      el(
        6,
        'Communication Number',
        'The second phone number, fax number or email address.'
      ),
      el(
        7,
        'Communication Number Qualifier',
        'What kind of number follows, for a third contact method.',
        {dataType: 'code', codes: COMMUNICATION_NUMBER_QUALIFIER_CODES}
      ),
      el(
        8,
        'Communication Number',
        'The third phone number, fax number or email address.'
      ),
    ],
  },

  TAX: {
    id: 'TAX',
    name: 'Tax Reference',
    purpose: 'Carries tax identification and exemption information.',
    loop: LoopId.Header,
    elements: [
      el(1, 'Tax Identification Number', 'The tax id being referenced.'),
      el(2, 'Tax Exempt Code', 'Whether the order is tax exempt.', {
        dataType: 'code',
        codes: [{code: 'X', label: 'Tax Exempt'}],
      }),
    ],
  },

  FOB: {
    id: 'FOB',
    name: 'F.O.B. Related Instructions',
    purpose: 'States who pays for shipment and where ownership/risk transfers.',
    loop: LoopId.Header,
    elements: [
      el(
        1,
        'Shipment Method of Payment',
        'Who is responsible for freight charges.',
        {
          dataType: 'code',
          required: true,
          codes: SHIPMENT_METHOD_OF_PAYMENT_CODES,
        }
      ),
      el(2, 'Location Qualifier', 'Where the F.O.B. point is.', {
        dataType: 'code',
        codes: [
          {code: 'OR', label: 'Origin (Shipping Point)'},
          {code: 'DE', label: 'Destination'},
          {code: 'ZZ', label: 'Mutually Defined'},
        ],
      }),
      el(3, 'Description', 'Free-form description of the F.O.B. point.'),
    ],
  },

  CTP: {
    id: 'CTP',
    name: 'Pricing Information',
    purpose:
      'Carries pricing detail beyond the base unit price, e.g. list price or a price basis.',
    loop: LoopId.Header,
    elements: [
      el(1, 'Class of Trade Code', 'The trade class this price applies to.', {
        dataType: 'code',
      }),
      el(2, 'Price Identifier Code', 'What kind of price this is.', {
        dataType: 'code',
        codes: PRICE_IDENTIFIER_CODES,
      }),
      el(3, 'Unit Price', 'The price value.', {dataType: 'numeric'}),
      el(4, 'Quantity', 'The quantity this price applies to.', {
        dataType: 'numeric',
      }),
      el(9, 'Basis of Unit Price Code', 'What the unit price is quoted per.', {
        dataType: 'code',
        codes: BASIS_OF_UNIT_PRICE_CODES,
      }),
    ],
  },

  ITD: {
    id: 'ITD',
    name: 'Terms of Sale/Deferred Terms of Sale',
    purpose:
      'States the payment terms — discount percent, due dates, and net terms.',
    loop: LoopId.Header,
    elements: [
      el(1, 'Terms Type Code', 'The category of payment terms.', {
        dataType: 'code',
        codes: [
          {code: '01', label: 'Basic'},
          {code: '02', label: 'End of Month'},
          {code: '03', label: 'Fixed Date'},
          {code: '04', label: 'Deferred/Installment'},
          {code: '05', label: 'Discount Not Applicable'},
          {code: '08', label: 'Basic Discount Offered'},
          {code: '09', label: 'Proximo'},
          {code: '12', label: '10 Days After End of Month (10 EOM)'},
          {code: '14', label: 'Previously agreed upon'},
        ],
      }),
      el(2, 'Terms Basis Date Code', 'What date the terms are measured from.', {
        dataType: 'code',
        codes: [
          {code: '1', label: 'Ship Date'},
          {code: '2', label: 'Delivery Date'},
          {code: '3', label: 'Invoice Date'},
          {code: '4', label: 'Specified Date'},
          {code: '5', label: 'Invoice Receipt Date'},
          {code: '15', label: 'Receipt of Goods'},
        ],
      }),
      el(3, 'Terms Discount Percent', 'Early-payment discount percentage.', {
        dataType: 'numeric',
      }),
      el(4, 'Terms Discount Due Date', 'Last date the discount applies.', {
        dataType: 'date',
      }),
      el(
        5,
        'Terms Discount Days Due',
        'Days from the basis date the discount applies.',
        {dataType: 'numeric'}
      ),
      el(6, 'Terms Net Due Date', 'Date full payment is due.', {
        dataType: 'date',
      }),
      el(7, 'Terms Net Days', 'Days from the basis date full payment is due.', {
        dataType: 'numeric',
      }),
      el(8, 'Terms Discount Amount', 'Early-payment discount amount.', {
        dataType: 'numeric',
      }),
    ],
  },

  DIS: {
    id: 'DIS',
    name: 'Discount Detail',
    purpose: 'Describes a quantity-based discount.',
    loop: LoopId.Header,
    elements: [
      el(1, 'Quantity', 'The quantity the discount applies to.', {
        dataType: 'numeric',
      }),
      el(
        2,
        'Unit or Basis for Measurement Code',
        'The unit that quantity is measured in.',
        {dataType: 'code'}
      ),
      el(3, 'Unit Price', 'The discounted unit price.', {dataType: 'numeric'}),
    ],
  },

  INC: {
    id: 'INC',
    name: 'Installment Information',
    purpose: 'Describes an installment payment/delivery plan.',
    loop: LoopId.Header,
    elements: [
      el(1, 'Installment Type Code', 'The kind of installment plan.', {
        dataType: 'code',
      }),
      el(2, 'Installment Number Code', 'Which installment this is.', {
        dataType: 'code',
      }),
    ],
  },

  DTM: {
    id: 'DTM',
    name: 'Date/Time Reference',
    purpose:
      'Carries a date and/or time tied to a specific meaning (requested ship date, delivery date, etc).',
    loop: LoopId.Header,
    elements: [
      el(1, 'Date/Time Qualifier', 'What this date/time means.', {
        dataType: 'code',
        required: true,
        codes: DATE_TIME_QUALIFIER_CODES,
      }),
      el(2, 'Date', 'The date.', {dataType: 'date', format: 'CCYYMMDD'}),
      el(3, 'Time', 'The time.', {dataType: 'time', format: 'HHMM'}),
    ],
  },

  LDT: {
    id: 'LDT',
    name: 'Lead Time',
    purpose: 'States a lead time associated with the order or an item.',
    loop: LoopId.Header,
    elements: [
      el(1, 'Lead Time Code', 'What the lead time measures.', {
        dataType: 'code',
      }),
      el(2, 'Lead Time Quantity', 'The lead time value.', {
        dataType: 'numeric',
      }),
      el(
        3,
        'Unit of Time Period or Interval Code',
        'The unit the lead time is measured in.',
        {dataType: 'code'}
      ),
    ],
  },

  MSG: {
    id: 'MSG',
    name: 'Message Text',
    purpose: 'A free-form note.',
    loop: LoopId.Header,
    elements: [el(1, 'Free-Form Message Text', 'The note text itself.')],
  },

  ISS: {
    id: 'ISS',
    name: 'Invoice Shipment Summary',
    purpose: 'Summarizes quantity and volume for shipment/invoicing purposes.',
    loop: LoopId.Header,
    elements: [
      el(1, 'Quantity', 'The quantity.', {dataType: 'numeric'}),
      el(
        2,
        'Unit or Basis for Measurement Code',
        'The unit that quantity is measured in.',
        {dataType: 'code'}
      ),
      el(3, 'Volume', 'The volume value.', {dataType: 'numeric'}),
    ],
  },

  PCT: {
    id: 'PCT',
    name: 'Percent Amount',
    purpose:
      'Carries a percentage tied to a specific meaning, e.g. an allowance or surcharge rate.',
    loop: LoopId.Header,
    elements: [
      el(1, 'Percent Qualifier', 'What this percentage means.', {
        dataType: 'code',
      }),
      el(2, 'Percent', 'The percentage value.', {dataType: 'numeric'}),
    ],
  },

  PWK: {
    id: 'PWK',
    name: 'Paperwork',
    purpose:
      'Identifies supporting paperwork associated with the order and how it will be sent.',
    loop: LoopId.Header,
    elements: [
      el(1, 'Report Type Code', 'The kind of supporting document.', {
        dataType: 'code',
        required: true,
        codes: [
          {code: 'AS', label: 'Additional Set of Data'},
          {code: 'CT', label: 'Certification'},
          {code: 'DA', label: 'Deposit Authorization'},
        ],
      }),
      el(2, 'Report Transmission Code', 'How the paperwork will be sent.', {
        dataType: 'code',
        codes: [
          {code: 'EM', label: 'Email'},
          {code: 'FX', label: 'Fax'},
          {code: 'AA', label: 'Available on Request at Buyer’s Site'},
        ],
      }),
    ],
  },

  N1: {
    id: 'N1',
    name: 'Name',
    purpose:
      'Identifies a party involved in the order — who they are and, optionally, their id. Opens a Name loop that N2/N3/N4/N5/N6/N9 attach to.',
    loop: LoopId.Name,
    required: true,
    elements: [
      el(1, 'Entity Identifier Code', 'Which party this is.', {
        dataType: 'code',
        required: true,
        codes: ENTITY_IDENTIFIER_CODES,
      }),
      el(2, 'Name', 'The party’s free-form name.'),
      el(
        3,
        'Identification Code Qualifier',
        'How the identification code is assigned.',
        {
          dataType: 'code',
          codes: IDENTIFICATION_CODE_QUALIFIER_CODES,
        }
      ),
      el(
        4,
        'Identification Code',
        'The party’s id, in the scheme named by element 3.'
      ),
    ],
  },

  N2: {
    id: 'N2',
    name: 'Additional Name Information',
    purpose:
      'Extra name-line text that would not fit in N102, e.g. a second name line.',
    loop: LoopId.Name,
    elements: [
      el(1, 'Name', 'Additional name text, line one.', {required: true}),
      el(2, 'Name', 'Additional name text, line two.'),
    ],
  },

  N3: {
    id: 'N3',
    name: 'Address Information',
    purpose: 'The street address for the party named in this Name loop.',
    loop: LoopId.Name,
    elements: [
      el(1, 'Address Information', 'Street address, line one.', {
        required: true,
      }),
      el(2, 'Address Information', 'Street address, line two.'),
    ],
  },

  N4: {
    id: 'N4',
    name: 'Geographic Location',
    purpose:
      'City, state, postal code and country for the party named in this Name loop.',
    loop: LoopId.Name,
    elements: [
      el(1, 'City Name', 'City.'),
      el(2, 'State or Province Code', 'State/province, postal abbreviation.', {
        dataType: 'code',
      }),
      el(3, 'Postal Code', 'ZIP or postal code.'),
      el(4, 'Country Code', 'ISO country code, when not domestic.', {
        dataType: 'code',
      }),
    ],
  },

  N5: {
    id: 'N5',
    name: 'Route',
    purpose:
      'Routing instructions associated with the party named in this Name loop.',
    loop: LoopId.Name,
    elements: [
      el(
        1,
        'Transportation Method/Type Code',
        'How the shipment should move.',
        {dataType: 'code'}
      ),
      el(2, 'Routing', 'Free-form routing description.'),
    ],
  },

  N6: {
    id: 'N6',
    name: 'Contact Employee Identification',
    purpose:
      'Identifies a specific employee contact tied to the party named in this Name loop.',
    loop: LoopId.Name,
    elements: [
      el(1, 'Employee Identification Number', 'The employee’s id.'),
      el(2, 'Employee Name', 'The employee’s name.'),
    ],
  },

  N9: {
    id: 'N9',
    name: 'Extended Reference Information',
    purpose:
      'A reference number tied to the party named in this Name loop, with room for a free-form description and a date.',
    loop: LoopId.Name,
    elements: [
      el(
        1,
        'Reference Number Qualifier',
        'What kind of reference number follows.',
        {
          dataType: 'code',
          required: true,
          codes: [
            {code: 'CT', label: 'Contract Number'},
            {code: 'ZZ', label: 'Mutually Defined'},
          ],
        }
      ),
      el(2, 'Reference Number', 'The reference number itself.', {
        required: true,
      }),
      el(3, 'Free-Form Description', 'Optional description of the reference.'),
      el(4, 'Date', 'Optional date tied to this reference.', {
        dataType: 'date',
      }),
      el(5, 'Time', 'Optional time tied to this reference.', {
        dataType: 'time',
      }),
    ],
  },

  LM: {
    id: 'LM',
    name: 'Code Source Information',
    purpose:
      'Names the code list authority for one or more LQ segments that follow it.',
    loop: LoopId.Header,
    elements: [
      el(
        1,
        'Agency Qualifier Code',
        'Which agency maintains the code list used below.',
        {
          dataType: 'code',
          required: true,
          codes: [
            {code: '42', label: 'Uniform Code Council (UCC)'},
            {code: 'ZZ', label: 'Mutually Defined'},
          ],
        }
      ),
    ],
  },

  LQ: {
    id: 'LQ',
    name: 'Industry Code',
    purpose:
      'Carries one code value from the list named by the preceding LM segment.',
    loop: LoopId.Header,
    elements: [
      el(1, 'Code List Qualifier Code', 'Which list this code is drawn from.', {
        dataType: 'code',
      }),
      el(2, 'Industry Code', 'The code value itself.', {required: true}),
    ],
  },
};
